import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { TID } from "@/constants/tid";
import GoogleAuthButton from "@/components/GoogleAuthButton";
import LogisticsNotice from "@/components/LogisticsNotice";
import PasswordInput from "@/components/PasswordInput";
import { track } from "@/lib/analytics";
import { api } from "@/lib/api";

function FoundersPromoBadge() {
  const [promo, setPromo] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/billing/founders-promo");
        if (!cancelled) setPromo(data.remaining > 0 ? data : null);
      } catch { /* hide */ }
    };
    load();
    const t = setInterval(load, 15000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  if (!promo) return null;
  return (
    <div data-testid="founders-promo-badge" className="mt-4 flex items-center gap-2 px-3 py-2 rounded-lg border border-[#FF3B30]/40 bg-[#FF3B30]/10 text-xs">
      <span className="font-black text-[#FF3B30] uppercase tracking-widest">Founders offer</span>
      <span className="text-neutral-300">First month FREE for the first {promo.cap} sellers — <span className="font-mono font-bold text-white">{promo.remaining} spot{promo.remaining === 1 ? "" : "s"} left</span></span>
    </div>
  );
}

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const refFromUrl = params.get("ref") || "";
  const nextParam = params.get("next");
  const safeNext = nextParam && nextParam.startsWith("/") ? nextParam : null;
  const [form, setForm] = useState({
    name: "",
    email: params.get("email") || "",
    password: "",
    role: params.get("role") === "seller" ? "seller" : "buyer",
    company: "",
    interests: "",
    country: (navigator.language?.split("-")[1] || "").toLowerCase(),
  });
  const [countries, setCountries] = useState([]);
  useEffect(() => { api.get("/discovery/countries").then((r) => setCountries(r.data.countries)).catch(() => {}); }, []);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [ack, setAck] = useState(false);
  const [digestOptIn, setDigestOptIn] = useState(false);

  useEffect(() => {
    if (refFromUrl) localStorage.setItem("vsk_ref", refFromUrl);
    const cmp = params.get("cmp");
    if (cmp) localStorage.setItem("vsk_cmp", cmp);
    if (params.get("plan") === "pro") localStorage.setItem("vsk_plan", "pro");
  }, [refFromUrl, params]);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const ref = refFromUrl || localStorage.getItem("vsk_ref") || null;
      const cmp = params.get("cmp") || localStorage.getItem("vsk_cmp") || null;
      const payload = {
        name: form.name,
        email: form.email,
        password: form.password,
        role: form.role,
        company: form.company || null,
        country: form.country && form.country.length === 2 ? form.country : null,
        interests: form.interests
          ? form.interests.split(",").map((s) => s.trim()).filter(Boolean)
          : [],
        ref,
        cmp,
        disclaimer_ack: ack,
        marketing_opt_in: form.role === "buyer" ? digestOptIn : false,
      };
      const u = await register(payload);
      await api.post("/legal/accept", { docs: u.role === "seller" ? ["terms", "privacy", "seller-agreement"] : ["terms", "privacy"], context: "register" }).catch(() => {});
      if (ref) localStorage.removeItem("vsk_ref");
      if (cmp) localStorage.removeItem("vsk_cmp");
      track("signup", { role: u.role });
      if (u.role === "buyer") {
        try { localStorage.setItem("vsk_new_buyer", "1"); } catch { /* ignore */ }
      }
      if (u.role === "buyer" && safeNext && safeNext.startsWith("/product/")) {
        track("buy_intent_signup", { product_id: safeNext.split("/product/")[1] });
      }
      const wantsPro = params.get("plan") === "pro" || localStorage.getItem("vsk_plan") === "pro";
      if (wantsPro) localStorage.removeItem("vsk_plan");
      nav(u.role === "seller" ? (wantsPro ? "/billing?from=join" : u.founders_promo ? "/founders-welcome" : "/seller") : (safeNext || "/discover"));
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-6 lg:p-12">
      <form onSubmit={submit} className="w-full max-w-md">
        <h1 className="font-black tracking-tight text-3xl">Create your account</h1>
        <p className="text-neutral-400 mt-2 text-sm">
          Pick a role. Buyers get AI-curated feeds. Sellers get always-on visibility on every SKU.
        </p>

        <FoundersPromoBadge />

        {(refFromUrl || (typeof window !== "undefined" && localStorage.getItem("vsk_ref"))) && (
          <div data-testid="register-ref-badge" className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#34C759]/40 bg-[#34C759]/10 text-xs">
            <span className="text-[#34C759]">Invited via referral</span>
            <span className="font-mono tracking-wider text-neutral-300">{(refFromUrl || localStorage.getItem("vsk_ref") || "").toUpperCase()}</span>
          </div>
        )}

        <div className="mt-6 grid grid-cols-2 gap-3">
          <RoleTile
            active={form.role === "buyer"}
            onClick={() => setForm({ ...form, role: "buyer" })}
            title="Buyer"
            body="AI-matched discovery"
            testId={TID.registerRoleBuyer}
          />
          <RoleTile
            active={form.role === "seller"}
            onClick={() => setForm({ ...form, role: "seller" })}
            title="Seller"
            body="Publish + analytics"
            testId={TID.registerRoleSeller}
          />
        </div>

        <div className="mt-6 space-y-4">
          <Field label="Full name">
            <Input required data-testid={TID.registerName} value={form.name} onChange={upd("name")}
              className="bg-[#121212] border-[#27272A]" placeholder="Alex Rivera" />
          </Field>
          <Field label="Work email">
            <Input required type="email" data-testid={TID.registerEmail} value={form.email} onChange={upd("email")}
              className="bg-[#121212] border-[#27272A]" placeholder="you@company.com" />
          </Field>
          <Field label="Password (min 6 chars)">
            <PasswordInput required minLength={6} data-testid={TID.registerPassword} value={form.password} onChange={upd("password")}
              className="bg-[#121212] border-[#27272A]" placeholder="••••••••" />
          </Field>
          <Field label="Country">
            <select required data-testid="register-country" value={form.country} onChange={upd("country")}
              className="flex h-10 w-full rounded-md bg-[#121212] border border-[#27272A] px-3 text-sm text-white focus:outline-none focus:border-[#FF3B30]">
              <option value="">Choose your country…</option>
              {countries.filter((c) => c.popular).map((c) => <option key={`p-${c.code}`} value={c.code}>{c.label}</option>)}
              <option disabled>──────────</option>
              {countries.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
            </select>
            <p className="text-[11px] text-neutral-500 mt-1">Sets your currency, payout options and where Buyer Finder looks first.</p>
          </Field>

          {form.role === "seller" ? (
            <Field label="Company (optional)">
              <Input data-testid={TID.registerCompany} value={form.company} onChange={upd("company")}
                className="bg-[#121212] border-[#27272A]" placeholder="Acme Trading Co." />
            </Field>
          ) : (
            <Field label="Interests (comma-separated)">
              <Input data-testid={TID.registerInterests} value={form.interests} onChange={upd("interests")}
                className="bg-[#121212] border-[#27272A]" placeholder="electronics, home decor, watches" />
            </Field>
          )}

          {err && (
            <div data-testid={TID.registerError} className="text-sm text-[#FF3B30] border border-[#FF3B30]/40 bg-[#FF3B30]/10 rounded-md px-3 py-2">
              {err}
            </div>
          )}

          {form.role === "buyer" && (
            <label className="flex items-start gap-3 cursor-pointer rounded-md border border-[#27272A] bg-[#121212] p-3.5 hover:border-[#3f3f46]">
              <input
                type="checkbox"
                checked={digestOptIn}
                onChange={(e) => setDigestOptIn(e.target.checked)}
                data-testid="register-digest-optin-checkbox"
                className="mt-0.5 h-4 w-4 accent-[#007AFF] flex-shrink-0"
              />
              <span className="text-xs text-neutral-400 leading-relaxed">
                Email me the <span className="text-white font-semibold">weekly digest</span> of the hottest
                drops (optional — unsubscribe anytime with one click).
              </span>
            </label>
          )}

          <label className="flex items-start gap-3 cursor-pointer rounded-md border border-[#27272A] bg-[#121212] p-3.5 hover:border-[#3f3f46]">
            <input
              type="checkbox"
              required
              checked={ack}
              onChange={(e) => setAck(e.target.checked)}
              data-testid="register-disclaimer-checkbox"
              className="mt-0.5 h-4 w-4 accent-[#007AFF] flex-shrink-0"
            />
            <span className="text-xs text-neutral-400 leading-relaxed">
              I agree to the{" "}
              <Link to="/terms" target="_blank" className="text-[#007AFF] hover:underline">Terms of Service</Link> and{" "}
              <Link to="/privacy" target="_blank" className="text-[#007AFF] hover:underline">Privacy Policy</Link>
              {form.role === "seller" && (
                <>
                  {" "}and the <Link to="/seller-agreement" target="_blank" className="text-[#007AFF] hover:underline">Seller Agreement</Link>. I understand VISMARKS is a{" "}
                  <span className="text-white font-semibold">visibility engine</span> and <span className="text-white font-semibold">does not guarantee sales</span>;
                  selling requires a Seller Plan, ID verification and a 0.5% fee per item sold
                </>
              )}
              {form.role !== "seller" && <>. I am at least 13 (with a guardian's consent if under 18)</>}.
            </span>
          </label>

          <Button
            type="submit"
            disabled={busy}
            data-testid={TID.registerSubmit}
            className="w-full bg-white text-black hover:bg-neutral-200 rounded-full h-11 font-semibold"
          >
            {busy ? "Creating account…" : "Create account"}
          </Button>

          {form.role === "buyer" && (
            <p className="text-[11px] text-neutral-500 leading-relaxed">
              By creating an account you agree to our{" "}
              <Link to="/legal" target="_blank" className="text-[#007AFF] hover:underline">Legal Notice</Link>.
              Delivery is arranged directly with each seller.
            </p>
          )}
          {form.role === "seller" && (
            <p className="text-[11px] text-neutral-500 leading-relaxed">
              By creating a seller account you agree that <span className="text-white">you are solely responsible for
              packing, shipping, delivery timelines, and returns</span> of every product you publish. Vismarks is
              a visibility &amp; sales platform, not a logistics provider.
            </p>
          )}

          <div className="relative py-1">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-[#0A0A0A] px-3 text-[10px] uppercase tracking-widest text-neutral-500">or</span>
            </div>
          </div>

          <GoogleAuthButton testId="register-google-btn" />

          <div className="text-sm text-neutral-500 text-center">
            Already have an account?{" "}
            <Link to="/login" className="text-white hover:underline">Sign in</Link>
          </div>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-neutral-400">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function RoleTile({ active, onClick, title, body, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`text-left rounded-md p-4 border ${active ? "border-[#007AFF] bg-[#007AFF]/10" : "border-[#27272A] bg-[#121212] hover:border-[#3f3f46]"}`}
      style={{ transitionProperty: "background-color, border-color", transitionDuration: "200ms" }}
    >
      <div className="font-bold text-white">{title}</div>
      <div className="text-xs text-neutral-400 mt-1">{body}</div>
    </button>
  );
}
